/**
 * Trend Control Components
 * Story: E007-S014 - Trend Indicators (↑↓→)
 * 
 * Components:
 * - TrendPeriodSelector: 7d/14d/30d dropdown
 * - TrendSortSelect: Sort by trend direction
 */

import React, { useState, useEffect } from 'react';
import { trendsApi, TrendSortOption, UserTrendPreferences } from '../lib/trendsApi';

// ============================================
// Period Selector Component
// ============================================

interface TrendPeriodSelectorProps {
  value: string;
  onChange: (period: string) => void;
  className?: string;
  persistToServer?: boolean;
}

const PERIOD_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'prev_month', label: 'Previous month' },
];

export const TrendPeriodSelector: React.FC<TrendPeriodSelectorProps> = ({
  value,
  onChange,
  className = '',
  persistToServer = true,
}) => {
  const [loading, setLoading] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPeriod = e.target.value;
    onChange(newPeriod);

    if (persistToServer) {
      try {
        setLoading(true);
        await trendsApi.updateUserPreferences({ preferred_period: newPeriod });
      } catch (error) {
        console.error('Failed to save period preference:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <label className="text-sm text-bodydark dark:text-bodydark1">
        Compare to:
      </label>
      <select
        value={value}
        onChange={handleChange}
        disabled={loading}
        className="rounded border border-stroke dark:border-strokedark bg-white dark:bg-boxdark px-3 py-1.5 text-sm text-black dark:text-white focus:border-primary focus:outline-none disabled:opacity-50"
      >
        {PERIOD_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {loading && (
        <svg className="w-4 h-4 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
    </div>
  );
};


// ============================================
// Sort By Trend Component
// ============================================

interface TrendSortSelectProps {
  value: TrendSortOption;
  onChange: (sort: TrendSortOption) => void;
  className?: string;
  metric?: 'roas' | 'ctr' | 'all';
}

const SORT_OPTIONS: { value: TrendSortOption; label: string; icon: string }[] = [
  { value: null, label: 'Default (A-Z)', icon: '⇅' },
  { value: 'roas_up', label: 'Best ROAS Improvement', icon: '↑' },
  { value: 'roas_down', label: 'Worst ROAS Decline', icon: '↓' },
  { value: 'ctr_up', label: 'Best CTR Improvement', icon: '↑' },
  { value: 'ctr_down', label: 'Worst CTR Decline', icon: '↓' },
  { value: 'alert', label: 'Alerts (>20% decline)', icon: '⚠️' },
];

export const TrendSortSelect: React.FC<TrendSortSelectProps> = ({
  value,
  onChange,
  className = '',
  metric = 'all',
}) => {
  // Filter options based on metric
  const filteredOptions = metric === 'all' 
    ? SORT_OPTIONS 
    : SORT_OPTIONS.filter(opt => 
        opt.value === null || 
        opt.value === 'alert' || 
        opt.value?.startsWith(metric)
      );

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <label className="text-sm text-bodydark dark:text-bodydark1">
        Sort by trend:
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange((e.target.value || null) as TrendSortOption)}
        className="rounded border border-stroke dark:border-strokedark bg-white dark:bg-boxdark px-3 py-1.5 text-sm text-black dark:text-white focus:border-primary focus:outline-none"
      >
        {filteredOptions.map((option) => (
          <option key={option.value || 'default'} value={option.value || ''}>
            {option.icon} {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};


// ============================================
// Combined Trend Controls Component
// ============================================

interface TrendControlsProps {
  period: string;
  onPeriodChange: (period: string) => void;
  sort: TrendSortOption;
  onSortChange: (sort: TrendSortOption) => void;
  className?: string;
  showAlertCount?: number;
}

export const TrendControls: React.FC<TrendControlsProps> = ({
  period,
  onPeriodChange,
  sort,
  onSortChange,
  className = '',
  showAlertCount,
}) => {
  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`}>
      <TrendPeriodSelector value={period} onChange={onPeriodChange} />
      
      <div className="h-6 w-px bg-stroke dark:bg-strokedark" />
      
      <TrendSortSelect value={sort} onChange={onSortChange} />
      
      {showAlertCount !== undefined && showAlertCount > 0 && (
        <>
          <div className="h-6 w-px bg-stroke dark:bg-strokedark" />
          <div className="flex items-center gap-2 px-3 py-1.5 bg-danger/10 text-danger rounded text-sm font-medium">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{showAlertCount} Alert{showAlertCount !== 1 ? 's' : ''}</span>
          </div>
        </>
      )}
    </div>
  );
};


// ============================================
// Hook for Trend Preferences
// ============================================

export const useTrendPreferences = () => {
  const [preferences, setPreferences] = useState<UserTrendPreferences>({
    preferred_period: '7d',
    show_sparklines: true,
    show_percentage: true,
    show_arrows: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await trendsApi.getUserPreferences();
        setPreferences(prefs);
      } catch (error) {
        console.error('Failed to load trend preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const updatePreferences = async (newPrefs: Partial<UserTrendPreferences>) => {
    try {
      await trendsApi.updateUserPreferences(newPrefs);
      setPreferences((prev) => ({ ...prev, ...newPrefs }));
    } catch (error) {
      console.error('Failed to update trend preferences:', error);
      throw error;
    }
  };

  return {
    preferences,
    loading,
    updatePreferences,
  };
};


export default TrendControls;
